import React, { useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { LayoutList, Send, MessagesSquare, BadgeDollarSign, Banknote, Target } from "lucide-react";
import "../../../styles/kanbans/swimlane.css";
import { MenuButton } from "../../atoms";
import DraggableJobCard from "./DraggableJobCard";

export type JobCard = {
  id: string;
  title: string;
  company: string;
  age?: string;
  logoUrl?: string;
  accent?: string;
};

export type Lane = {
  id: string;
  title: string;
  icon: string;
  jobs: number;
  cards?: JobCard[];
};

type Props = {
  lane: Lane;
  headerOffset?: number;
  onRename: (id: string, nextTitle: string) => void | Promise<void>;
  onMove: (id: string) => void;
  onDelete: (id: string) => void;
  onAddCard?: () => void;
  onDeleteCard?: (cardId: string) => void;
  onCardClick?: (cardId: string) => void;
};

export default function Swimlane({
  lane,
  headerOffset = 180,
  onRename,
  onMove,
  onDelete,
  onAddCard,
  onDeleteCard,
  onCardClick,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState(lane.title);
  const { setNodeRef, isOver } = useDroppable({ id: lane.id });

  const menuItems = [
    {
      label: 'Move list',
      onClick: () => onMove(lane.id),
    },
    {
      label: 'Delete list',
      onClick: () => onDelete(lane.id),
      danger: true,
    },
  ];

  const renderLaneIcon = () => {
    const key = (lane.icon || "").toLowerCase();
    const titleUpper = lane.title.toUpperCase();

    if (key === "wishlist" || titleUpper.includes("WISH")) {
      return <LayoutList size={16} />;
    }
    if (key === "applied" || titleUpper.includes("APPLY")) {
      return <Send size={16} />;
    }
    if (key === "interview" || titleUpper.includes("INTERVIEW")) {
      return <MessagesSquare size={16} />;
    }
    if (key === "offer" || titleUpper.includes("OFFER")) {
      return <BadgeDollarSign size={16} />;
    }
    if (key === "rejected" || titleUpper.includes("REJECT")) {
      return <Banknote size={16} />;
    }
    return <Target size={16} />;
  };

  const commit = async () => {
    const trimmedTitle = titleDraft.trim();
    if (!trimmedTitle) {
      setTitleDraft(lane.title);
      setEditing(false);
      return;
    }

    try {
      await onRename(lane.id, trimmedTitle);
      setEditing(false);
    } catch (error) {

    }
  };

  const cancel = () => {
    setTitleDraft(lane.title);
    setEditing(false);
  };

  return (
    <article className={`lane ${isOver ? "lane--dragover" : ""}`} role="listitem">
      <header className="lane__header">
        <span className="lane__icon" aria-hidden>
          {renderLaneIcon()}
        </span>

        {editing ? (
          <div className="lane__titleEdit">
            <input
              className="lane__titleInput"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                if (e.key === "Escape") cancel();
              }}
              onBlur={commit}
              autoFocus
              placeholder="List title"
            />
            <button
              type="button"
              className="lane__titleBtn"
              onMouseDown={(e) => e.preventDefault()}
              onClick={commit}
              aria-label="Save title"
              title="Save"
            >
              ✓
            </button>
            <button
              type="button"
              className="lane__titleBtn"
              onMouseDown={(e) => e.preventDefault()}
              onClick={cancel}
              aria-label="Cancel editing"
              title="Cancel"
            >
              ✕
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="lane__titleButton"
            onClick={() => setEditing(true)}
            title="Rename list"
          >
            <span className="lane__titleText">{lane.title.toUpperCase()}</span>
          </button>
        )}

        <div className="lane__menuWrap" onClick={(e) => e.stopPropagation()}>
          <MenuButton menuItems={menuItems} />
        </div>
      </header>

      <div className="lane__subtitle" aria-hidden />
      <div className="lane__cards" ref={setNodeRef}>
        {lane.cards?.map((card) => (
          <DraggableJobCard
            key={card.id}
            cardId={card.id}
            laneId={lane.id}
            title={card.title}
            company={card.company}
            age={card.age}
            logoUrl={card.logoUrl}
            accent={card.accent}
            onDeleteClick={() => onDeleteCard?.(card.id)}
            onClick={() => onCardClick?.(card.id)}
          />
        ))}
      </div>
      <button type="button" className="lane__addCard" onClick={onAddCard}>
        +
      </button>
    </article>
  );
}